interface IReconnectableConnection {
  isOnline: () => boolean,
  close: () => void,
  reconnect: () => void,
  onDisconnect: (handler: () => void) => unknown,
  offDisconnect: (handler: () => void) => void,
}

const SUSPENSION_CHECK_INTERVAL = 10 * 1000;

// Longer than the one-minute wake-up of timers in hidden tabs, so only a suspended page (usually a sleeping
// computer) produces such a gap. The socket server pings every 25 s and closes a session whose pong is 10 s
// late, so after a suspension this long the server has already dropped the session.
const SUSPENSION_THRESHOLD = 2 * 60 * 1000;

/**
 * Reconnects the socket after the computer wakes from sleep.
 *
 * The server drops the session of a sleeping computer, but its close never reaches the browser: after
 * wake-up the WebSocket still looks open, nothing arrives and `onclose` never fires, so the SDK never
 * reconnects and live tables freeze until reload. Heartbeats are WebSocket pings answered by the browser
 * itself, so JS cannot watch them; a long gap between timer ticks is the signal instead. `Date.now()` is
 * used because the monotonic clock may stop during sleep.
 *
 * The socket is dropped the way a network failure would drop it, so the SDK re-subscribes and the Active
 * Calendar refetches on reconnect. `close()` disables auto-reconnect only for the socket it closes, and
 * reconnecting before that socket's close event would flush queued messages into a still-connecting
 * socket, so the new socket is opened only once the old one has closed.
 */
const reconnectAfterSuspension = (connection: IReconnectableConnection): void => {
  let lastCheckAt = Date.now();

  const restart = (): void => {
    const reconnectOnceClosed = (): void => {
      connection.offDisconnect(reconnectOnceClosed);
      connection.reconnect();
    };

    connection.onDisconnect(reconnectOnceClosed);
    connection.close();
  };

  setInterval(() => {
    const now = Date.now();
    const wasSuspended = now - lastCheckAt > SUSPENSION_THRESHOLD;

    lastCheckAt = now;

    if (wasSuspended && connection.isOnline()) {
      restart();
    }
  }, SUSPENSION_CHECK_INTERVAL);
};

export default reconnectAfterSuspension;
